import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabaseClient';
import NotificationBell from '../../components/admin/NotificationBell';
import MessageNotificationBell from '../../components/admin/MessageNotificationBell';
import ConfirmDialog from '../../components/common/ConfirmDialog';

interface BonusTier {
  id: string;
  min_amount: number;
  bonus_percent: number;
  is_active: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
}

interface CustomerProfile {
  id: string;
  full_name: string;
  email: string;
  wallet_balance: number;
}

interface Toast {
  id: number;
  message: string;
  type: 'success' | 'error' | 'info';
}

const Toast = ({ toast, onClose }: { toast: Toast; onClose: () => void }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const bgColor = toast.type === 'success'
    ? 'bg-green-500'
    : toast.type === 'error'
      ? 'bg-red-500'
      : 'bg-blue-500';

  return (
    <div className={`${bgColor} text-white px-4 py-3 rounded shadow-lg flex justify-between items-center`}>
      <span>{toast.message}</span>
      <button onClick={onClose} className="ml-4 text-white focus:outline-none">
        ×
      </button>
    </div>
  );
};

const WalletManagement: React.FC = () => {
  // Toast state
  const [toasts, setToasts] = useState<Toast[]>([]);

  // Bonus tiers state
  const [bonusTiers, setBonusTiers] = useState<BonusTier[]>([]);
  const [isLoadingTiers, setIsLoadingTiers] = useState(true);
  const [editingTier, setEditingTier] = useState<BonusTier | null>(null);
  const [isAddingTier, setIsAddingTier] = useState(false);
  const [tierForm, setTierForm] = useState({ min_amount: '', bonus_percent: '' });
  const [deleteTierTarget, setDeleteTierTarget] = useState<BonusTier | null>(null);

  // Manual top-up state
  const [customerSearch, setCustomerSearch] = useState('');
  const [searchResults, setSearchResults] = useState<CustomerProfile[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerProfile | null>(null);
  const [topUpAmount, setTopUpAmount] = useState('');
  const [topUpNote, setTopUpNote] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [isProcessingTopUp, setIsProcessingTopUp] = useState(false);

  // Toast functions
  const addToast = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
  };

  const removeToast = (id: number) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  // Fetch bonus tiers
  const fetchBonusTiers = async () => {
    setIsLoadingTiers(true);
    try {
      const { data, error } = await supabase
        .from('wallet_bonus_tiers')
        .select('*')
        .order('min_amount', { ascending: true });

      if (error) throw error;
      setBonusTiers(data || []);
    } catch (err: any) {
      addToast(`Failed to load bonus tiers: ${err.message}`, 'error');
    } finally {
      setIsLoadingTiers(false);
    }
  };

  useEffect(() => {
    fetchBonusTiers();
  }, []);

  // Search customers
  const searchCustomers = async (query: string) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('id, full_name, email, wallet_balance')
        .eq('role', 'customer')
        .or(`full_name.ilike.%${query}%,email.ilike.%${query}%`)
        .limit(10);

      if (error) throw error;
      setSearchResults(data || []);
    } catch (err: any) {
      addToast(`Search failed: ${err.message}`, 'error');
    } finally {
      setIsSearching(false);
    }
  };

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (customerSearch) {
        searchCustomers(customerSearch);
      } else {
        setSearchResults([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [customerSearch]);

  // Add or update tier
  const handleSaveTier = async () => {
    const minAmount = parseFloat(tierForm.min_amount);
    const bonusPercent = parseInt(tierForm.bonus_percent);

    if (isNaN(minAmount) || minAmount < 0) {
      addToast('Please enter a valid minimum amount', 'error');
      return;
    }

    if (isNaN(bonusPercent) || bonusPercent < 0 || bonusPercent > 100) {
      addToast('Please enter a valid bonus percent (0-100)', 'error');
      return;
    }

    try {
      if (editingTier) {
        const { error } = await supabase
          .from('wallet_bonus_tiers')
          .update({
            min_amount: minAmount,
            bonus_percent: bonusPercent,
            bonus_amount: null,  // Clear fixed amount so it calculates from percent
            updated_at: new Date().toISOString()
          })
          .eq('id', editingTier.id);

        if (error) throw error;
        addToast('Tier updated successfully', 'success');
      } else {
        const { error } = await supabase
          .from('wallet_bonus_tiers')
          .insert({
            min_amount: minAmount,
            bonus_percent: bonusPercent,
            bonus_amount: null,  // Use percentage-based calculation
            is_active: true
          });

        if (error) throw error;
        addToast('Tier added successfully', 'success');
      }

      setTierForm({ min_amount: '', bonus_percent: '' });
      setEditingTier(null);
      setIsAddingTier(false);
      await fetchBonusTiers();
    } catch (err: any) {
      addToast(`Failed to save tier: ${err.message}`, 'error');
    }
  };

  // Delete tier
  const handleDeleteTier = async () => {
    if (!deleteTierTarget) return;

    try {
      const { error } = await supabase
        .from('wallet_bonus_tiers')
        .delete()
        .eq('id', deleteTierTarget.id);

      if (error) throw error;
      addToast('Tier deleted successfully', 'success');
      setDeleteTierTarget(null);
      await fetchBonusTiers();
    } catch (err: any) {
      addToast(`Failed to delete tier: ${err.message}`, 'error');
    }
  };

  // Toggle tier active status
  const handleToggleTierActive = async (tier: BonusTier) => {
    try {
      const { error } = await supabase
        .from('wallet_bonus_tiers')
        .update({
          is_active: !tier.is_active,
          updated_at: new Date().toISOString()
        })
        .eq('id', tier.id);

      if (error) throw error;
      addToast(`Tier ${tier.is_active ? 'deactivated' : 'activated'} successfully`, 'success');
      await fetchBonusTiers();
    } catch (err: any) {
      addToast(`Failed to update tier: ${err.message}`, 'error');
    }
  };

  // Process manual top-up
  const handleManualTopUp = async () => {
    if (!selectedCustomer) {
      addToast('Please select a customer', 'error');
      return;
    }

    const amount = parseFloat(topUpAmount);
    if (isNaN(amount) || amount <= 0) {
      addToast('Please enter a valid positive amount', 'error');
      return;
    }

    setIsProcessingTopUp(true);
    try {
      // Get current user (admin) for created_by field
      const { data: { user: adminUser } } = await supabase.auth.getUser();
      if (!adminUser) throw new Error('Admin user not found');

      const newBalance = (selectedCustomer.wallet_balance || 0) + amount;

      // Create transaction record
      const { error: transactionError } = await supabase
        .from('wallet_transactions')
        .insert({
          user_id: selectedCustomer.id,
          type: 'adjustment',
          amount: amount,
          balance_after: newBalance,
          description: topUpNote || 'Admin adjustment',
          created_by: adminUser.id
        });

      if (transactionError) throw transactionError;

      // Update user's wallet balance
      const { error: updateError } = await supabase
        .from('user_profiles')
        .update({ wallet_balance: newBalance })
        .eq('id', selectedCustomer.id);

      if (updateError) throw updateError;

      addToast(
        `Successfully added $${amount.toFixed(2)} to ${selectedCustomer.full_name}'s wallet. New balance: $${newBalance.toFixed(2)}`,
        'success'
      );

      // Reset form
      setSelectedCustomer(null);
      setCustomerSearch('');
      setTopUpAmount('');
      setTopUpNote('');
      setSearchResults([]);
    } catch (err: any) {
      addToast(`Failed to process top-up: ${err.message}`, 'error');
    } finally {
      setIsProcessingTopUp(false);
    }
  };

  return (
    <div className="space-y-6 pt-2">
      {/* Toast container */}
      <div className="fixed bottom-4 right-4 z-50 space-y-2">
        {toasts.map(toast => (
          <Toast
            key={toast.id}
            toast={toast}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </div>

      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-semibold text-[#344854]">Wallet Management</h1>
        <div className="flex items-center gap-3">
          <MessageNotificationBell />
          <NotificationBell />
        </div>
      </div>

      {/* Two column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Section A: Bonus Tiers Management */}
        <div className="bg-white rounded-lg shadow p-5">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-medium">Bonus Tiers</h2>
            <button
              onClick={() => {
                setIsAddingTier(true);
                setEditingTier(null);
                setTierForm({ min_amount: '', bonus_percent: '' });
              }}
              className="bg-[#3A5E63] text-white px-3 py-1.5 rounded text-sm hover:bg-[#2a4548] transition-colors"
            >
              + Add Tier
            </button>
          </div>

          <p className="text-sm text-gray-500 mb-4">
            Configure bonus percentages for wallet top-ups. Customers get bonus credits based on the tier their top-up amount qualifies for.
          </p>

          {/* Add/Edit Tier Form */}
          {(isAddingTier || editingTier) && (
            <div className="bg-gray-50 p-4 rounded-lg mb-4 border">
              <h3 className="font-medium mb-3">
                {editingTier ? 'Edit Tier' : 'Add New Tier'}
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    Minimum Amount ($)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={tierForm.min_amount}
                    onChange={e => setTierForm({ ...tierForm, min_amount: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#3A5E63]"
                    placeholder="e.g., 500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    Bonus Percent (%)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="1"
                    value={tierForm.bonus_percent}
                    onChange={e => setTierForm({ ...tierForm, bonus_percent: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#3A5E63]"
                    placeholder="e.g., 20"
                  />
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <button
                  onClick={handleSaveTier}
                  className="bg-[#3A5E63] text-white px-4 py-2 rounded hover:bg-[#2a4548] transition-colors"
                >
                  {editingTier ? 'Update' : 'Add'}
                </button>
                <button
                  onClick={() => {
                    setIsAddingTier(false);
                    setEditingTier(null);
                    setTierForm({ min_amount: '', bonus_percent: '' });
                  }}
                  className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Tiers List */}
          {isLoadingTiers ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-16 bg-gray-100 animate-pulse rounded"></div>
              ))}
            </div>
          ) : bonusTiers.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <div className="text-3xl mb-2">📊</div>
              <p>No bonus tiers configured</p>
              <p className="text-sm">Add tiers to offer bonuses on wallet top-ups</p>
            </div>
          ) : (
            <div className="space-y-2">
              {bonusTiers.map((tier) => (
                <div
                  key={tier.id}
                  className={`p-3 border rounded-lg flex items-center justify-between ${
                    tier.is_active ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-200 opacity-60'
                  }`}
                >
                  <div>
                    <div className="font-medium">
                      ${tier.min_amount.toLocaleString()}+ → {tier.bonus_percent}% bonus
                    </div>
                    <div className="text-sm text-gray-500">
                      e.g., ${tier.min_amount} top-up = ${(tier.min_amount * tier.bonus_percent / 100).toFixed(0)} bonus
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleToggleTierActive(tier)}
                      className={`px-2 py-1 rounded text-xs ${
                        tier.is_active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {tier.is_active ? 'Active' : 'Inactive'}
                    </button>
                    <button
                      onClick={() => {
                        setEditingTier(tier);
                        setIsAddingTier(false);
                        setTierForm({
                          min_amount: tier.min_amount.toString(),
                          bonus_percent: tier.bonus_percent.toString()
                        });
                      }}
                      className="text-blue-600 hover:text-blue-800 text-sm"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => setDeleteTierTarget(tier)}
                      className="text-red-600 hover:text-red-800 text-sm"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Section B: Manual Wallet Top-Up */}
        <div className="bg-white rounded-lg shadow p-5">
          <h2 className="text-lg font-medium mb-4">Manual Wallet Top-Up</h2>

          <p className="text-sm text-gray-500 mb-4">
            Add funds directly to a customer's wallet. This creates an adjustment transaction for audit purposes.
          </p>

          {/* Customer Search */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search Customer
            </label>
            <div className="relative">
              <input
                type="text"
                value={customerSearch}
                onChange={e => {
                  setCustomerSearch(e.target.value);
                  setSelectedCustomer(null);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#3A5E63]"
                placeholder="Search by name or email..."
              />
              {isSearching && (
                <div className="absolute right-3 top-2.5">
                  <div className="w-5 h-5 border-2 border-[#3A5E63] border-t-transparent rounded-full animate-spin"></div>
                </div>
              )}
            </div>

            {/* Search Results */}
            {searchResults.length > 0 && !selectedCustomer && (
              <div className="mt-2 border border-gray-200 rounded-md max-h-48 overflow-y-auto">
                {searchResults.map(customer => (
                  <button
                    key={customer.id}
                    onClick={() => {
                      setSelectedCustomer(customer);
                      setCustomerSearch(customer.full_name || customer.email);
                      setSearchResults([]);
                    }}
                    className="w-full px-3 py-2 text-left hover:bg-gray-50 border-b last:border-b-0 flex justify-between items-center"
                  >
                    <div>
                      <div className="font-medium">{customer.full_name || 'No name'}</div>
                      <div className="text-sm text-gray-500">{customer.email}</div>
                    </div>
                    <div className="text-sm text-gray-600">
                      Balance: ${(customer.wallet_balance || 0).toFixed(2)}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Selected Customer Display */}
          {selectedCustomer && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex justify-between items-center">
                <div>
                  <div className="font-medium">{selectedCustomer.full_name}</div>
                  <div className="text-sm text-gray-600">{selectedCustomer.email}</div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-gray-500">Current Balance</div>
                  <div className="font-semibold text-lg">
                    ${(selectedCustomer.wallet_balance || 0).toFixed(2)}
                  </div>
                </div>
              </div>
              <button
                onClick={() => {
                  setSelectedCustomer(null);
                  setCustomerSearch('');
                }}
                className="mt-2 text-sm text-blue-600 hover:text-blue-800"
              >
                Change customer
              </button>
            </div>
          )}

          {/* Top-up Amount */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Amount to Add ($)
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={topUpAmount}
              onChange={e => setTopUpAmount(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#3A5E63]"
              placeholder="e.g., 50.00"
            />
          </div>

          {/* Note */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Note (optional)
            </label>
            <input
              type="text"
              value={topUpNote}
              onChange={e => setTopUpNote(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#3A5E63]"
              placeholder="e.g., Promotional credit, refund, etc."
            />
          </div>

          {/* Preview */}
          {selectedCustomer && topUpAmount && parseFloat(topUpAmount) > 0 && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="text-sm text-gray-600 mb-1">After top-up:</div>
              <div className="font-semibold text-xl text-green-700">
                ${((selectedCustomer.wallet_balance || 0) + parseFloat(topUpAmount)).toFixed(2)}
              </div>
            </div>
          )}

          {/* Submit Button */}
          <button
            onClick={handleManualTopUp}
            disabled={!selectedCustomer || !topUpAmount || parseFloat(topUpAmount) <= 0 || isProcessingTopUp}
            className="w-full bg-[#3A5E63] text-white px-4 py-3 rounded-lg hover:bg-[#2a4548] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {isProcessingTopUp ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                Processing...
              </>
            ) : (
              'Add to Wallet'
            )}
          </button>
        </div>
      </div>

      {/* Delete Tier Confirmation Dialog */}
      <ConfirmDialog
        isOpen={!!deleteTierTarget}
        title="Delete Bonus Tier?"
        message={[
          `Delete the tier:`,
          `$${deleteTierTarget?.min_amount || 0}+ → ${deleteTierTarget?.bonus_percent || 0}% bonus`,
          '',
          'This action cannot be undone.'
        ]}
        confirmText="Delete"
        cancelText="Cancel"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
        onConfirm={handleDeleteTier}
        onCancel={() => setDeleteTierTarget(null)}
      />
    </div>
  );
};

export default WalletManagement;
